import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API, { apis } from "common/apis";
import { IAttendanceRosterListAPI } from "common/interface/IAttendanceRoster";
import { IAttendanceRosterUpload } from "common/interface/IAttendanceRosterUpload";
import { logoutIfNeeded } from "common/utils";
import {
  getPersistedProcessingAttendanceRoster,
  persistProcessingAttendanceRoster,
  removePersistedProcessingAttendanceRoster,
} from "services/PersistService/PersistAttendanceRosterService";
import { RootState } from "store/store";

export interface AttendanceRosterState {
  processingAttendanceRoster: IAttendanceRosterUpload;
  attendanceRosters: IAttendanceRosterListAPI;
  status: "loading" | "success" | "error" | "idle" | "cancelling" | "cancelled";
  listStatus: "idle" | "loading" | "error";
  error?: string;
  listError?: string;
}

const emptyList: IAttendanceRosterListAPI = {
  pageNo: 1,
  pageSize: 25,
  pages: 0,
  total: 0,
  data: [],
};

const getInitialProcessingAttendanceRoster = (): IAttendanceRosterUpload => {
  const persisted = getPersistedProcessingAttendanceRoster();
  // Only keep staged uploads that still have a confirm key.
  if (persisted?.key) {
    return persisted;
  }
  if (Object.keys(persisted || {}).length) {
    removePersistedProcessingAttendanceRoster();
  }
  return {};
};

const initialState: AttendanceRosterState = {
  processingAttendanceRoster: getInitialProcessingAttendanceRoster(),
  attendanceRosters: emptyList,
  status: "idle",
  listStatus: "idle",
};

const getApiErrorMessage = (error: any, fallback = "Something went wrong!") => {
  const data = error?.response?.data;
  if (data?.errors?.length) {
    return data.errors.map((e: { message?: string }) => e.message).filter(Boolean).join(", ");
  }
  return data?.message || fallback;
};

export const getAttendanceRosters = createAsyncThunk<
  IAttendanceRosterListAPI,
  { pageNo: number; pageSize: number; filter: string }
>(
  "attendanceRoster/list",
  async ({ pageNo = 1, pageSize = 25, filter }, thunkApi) => {
    try {
      const filterParams = filter ? Object.fromEntries(new URLSearchParams(filter)) : {};
      const response = await API.get(apis.getAttendanceRosters, {
        params: {
          pageNo,
          pageSize,
          ...filterParams,
        },
      });
      return response.data as IAttendanceRosterListAPI;
    } catch (error: any) {
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: getApiErrorMessage(error) });
    }
  }
);

export const saveProcessingAttendanceRoster = createAsyncThunk(
  "attendanceRoster/upload/save",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { key } = state.attendanceRoster.processingAttendanceRoster;
    if (!key) {
      return thunkApi.rejectWithValue({
        message: "Uploaded data not found or it is expired.",
      });
    }
    try {
      const response = await API.post(apis.saveAttendanceRoster, {}, { params: { key } });
      return response.data;
    } catch (error: any) {
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: getApiErrorMessage(error) });
    }
  }
);

export const cancelProcessingAttendanceRoster = createAsyncThunk(
  "attendanceRoster/upload/cancel",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { key } = state.attendanceRoster.processingAttendanceRoster;
    if (!key) {
      return thunkApi.rejectWithValue({
        message: "Unable to cancel bulk upload request.",
      });
    }
    try {
      const response = await API.post(
        apis.saveAttendanceRoster,
        {},
        { params: { key, cancel: 1 } }
      );
      return response.data;
    } catch (error: any) {
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: getApiErrorMessage(error) });
    }
  }
);

const attendanceRosterSlice = createSlice({
  name: "attendanceRoster",
  initialState,
  reducers: {
    setProcessingAttendanceRoster: (state, action) => {
      state.processingAttendanceRoster = {
        ...action.payload,
        timeUploaded: new Date().toISOString(),
      };
      state.status = "idle";
      state.error = undefined;
      // Persist only fully-valid staged uploads (key present).
      if (action.payload?.key) {
        persistProcessingAttendanceRoster(state.processingAttendanceRoster);
      } else {
        removePersistedProcessingAttendanceRoster();
      }
    },
    removeProcessingAttendanceRoster: (state) => {
      state.processingAttendanceRoster = {};
      state.status = "idle";
      state.error = undefined;
      removePersistedProcessingAttendanceRoster();
    },
    resetAttendanceRosterStatus: (state) => {
      state.status = "idle";
      state.error = undefined;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getAttendanceRosters.pending, (state) => {
        state.listStatus = "loading";
        state.listError = undefined;
      })
      .addCase(getAttendanceRosters.fulfilled, (state, action) => {
        state.listStatus = "idle";
        state.attendanceRosters = action.payload;
      })
      .addCase(getAttendanceRosters.rejected, (state, action) => {
        state.listStatus = "error";
        state.listError = (action.payload as { message: string })?.message;
      })
      .addCase(saveProcessingAttendanceRoster.pending, (state) => {
        state.status = "loading";
        state.error = undefined;
      })
      .addCase(saveProcessingAttendanceRoster.fulfilled, (state) => {
        state.status = "success";
        state.processingAttendanceRoster = {};
        removePersistedProcessingAttendanceRoster();
      })
      .addCase(saveProcessingAttendanceRoster.rejected, (state, action) => {
        state.status = "error";
        state.error = (action.payload as { message: string })?.message;
      })
      .addCase(cancelProcessingAttendanceRoster.pending, (state) => {
        state.status = "cancelling";
        state.error = undefined;
      })
      .addCase(cancelProcessingAttendanceRoster.fulfilled, (state) => {
        state.status = "cancelled";
        state.processingAttendanceRoster = {};
        removePersistedProcessingAttendanceRoster();
      })
      .addCase(cancelProcessingAttendanceRoster.rejected, (state, action) => {
        state.status = "error";
        state.error = (action.payload as { message: string })?.message;
      });
  },
});

export const {
  setProcessingAttendanceRoster,
  removeProcessingAttendanceRoster,
  resetAttendanceRosterStatus,
} = attendanceRosterSlice.actions;
export default attendanceRosterSlice.reducer;
